import { useEffect, useMemo, useRef, useState } from 'react';
import { OrbitCatalogEnum } from '../../../../shared/types/orbit';
import { OrbitGenderEnum } from '../constants/citizenCatalogs';
import { useOrbitCatalog } from '../hooks/useOrbitCatalog';
import { useOrbitFormSection } from '../hooks/useOrbitFormSection';
import {
    initialValues,
    isCcDocumentType,
    isJuridicalPersonType,
    validationSchema,
} from './formData';
import { OrbitCitizenInfoProps } from '../sectionProps';
import {
    ORBIT_ACCOUNT_LOOKUP_DEBOUNCE_MS,
    ORBIT_ACCOUNT_MIN_DOCUMENT_LENGTH,
} from '../constants/accountLookup';

export const useOrbitCitizenInfo = ({
    onChangeFormik,
    onAccountLookup,
    readOnly,
    seedValues,
    seededDocumentType,
    seededDocument,
    accountLookupCatalogsReady = true,
}: OrbitCitizenInfoProps) => {
    const { formik } = useOrbitFormSection({
        initialValues,
        validationSchema,
        onChangeFormik,
        readOnly,
        seedValues,
    });

    const documentSeeded = useRef(false);

    useEffect(() => {
        if (documentSeeded.current || readOnly) return;
        if (!seededDocument || seededDocument === formik.values.document) {
            return;
        }
        documentSeeded.current = true;
        formik.setFieldValue('document', seededDocument);
    }, [seededDocument, readOnly, formik.values.document]);

    const {
        items: typesDocument,
        loading: loadingDocumentTypes,
        error: documentTypesError,
    } = useOrbitCatalog(OrbitCatalogEnum.DocumentTypes);

    const documentTypeSeeded = useRef(false);

    useEffect(() => {
        if (documentTypeSeeded.current || readOnly) return;
        if (!seededDocumentType || loadingDocumentTypes) return;
        if (seededDocumentType === formik.values.typeDocument) {
            documentTypeSeeded.current = true;
            return;
        }

        const isValidDocumentType = typesDocument.some(
            (type) => String(type.id) === seededDocumentType
        );

        if (!isValidDocumentType) return;

        documentTypeSeeded.current = true;
        formik.setFieldValue('typeDocument', seededDocumentType);
    }, [
        seededDocumentType,
        readOnly,
        loadingDocumentTypes,
        typesDocument,
        formik.values.typeDocument,
    ]);

    const {
        items: typesPerson,
        loading: loadingPersonTypes,
        error: personTypesError,
    } = useOrbitCatalog(OrbitCatalogEnum.PersonTypes);

    const {
        items: orbitGenders,
        loading: loadingGenders,
        error: gendersError,
    } = useOrbitCatalog(OrbitCatalogEnum.Genders);

    const isJuridical = useMemo(
        () => isJuridicalPersonType(formik.values.personType),
        [formik.values.personType]
    );

    const genders = useMemo(() => {
        if (isJuridical) return [];

        return orbitGenders.filter(
            (g) =>
                g.code !== OrbitGenderEnum.NoAplica &&
                g.id !== OrbitGenderEnum.NoAplica
        );
    }, [isJuridical, orbitGenders]);

    useEffect(() => {
        if (readOnly) return;

        if (isJuridical) {
            if (formik.values.gender !== '') {
                formik.setFieldValue('gender', '');
            }
            if (formik.values.lastName !== '') {
                formik.setFieldValue('lastName', '');
            }
            if (formik.values.isLGBTIQ) {
                formik.setFieldValue('isLGBTIQ', false);
            }
            return;
        }

        if (formik.values.gender === OrbitGenderEnum.NoAplica) {
            formik.setFieldValue('gender', '');
        }
    }, [
        isJuridical,
        formik.values.gender,
        formik.values.lastName,
        formik.values.isLGBTIQ,
    ]);

    useEffect(() => {
        if (readOnly) return;

        if (!isCcDocumentType(formik.values.typeDocument)) return;

        const { document } = formik.values;

        if (document && Number.isNaN(Number(document))) {
            formik.setFieldValue('document', '');
            formik.setFieldError(
                'document',
                'El documento debe contener solo números'
            );
        }
    }, [formik.values.typeDocument, formik.values.document, readOnly]);

    const onAccountLookupRef = useRef(onAccountLookup);
    onAccountLookupRef.current = onAccountLookup;

    const [isLookupDebouncing, setIsLookupDebouncing] = useState(false);

    useEffect(() => {
        if (
            readOnly ||
            !onAccountLookupRef.current ||
            !accountLookupCatalogsReady
        ) {
            return;
        }

        const typeDocument = formik.values.typeDocument?.trim();
        const document = formik.values.document?.trim();

        if (
            !typeDocument ||
            !document ||
            document.length < ORBIT_ACCOUNT_MIN_DOCUMENT_LENGTH
        ) {
            setIsLookupDebouncing(false);
            return;
        }

        setIsLookupDebouncing(true);

        const timer = window.setTimeout(() => {
            setIsLookupDebouncing(false);
            onAccountLookupRef.current?.({
                typeDocument,
                document,
            });
        }, ORBIT_ACCOUNT_LOOKUP_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
            setIsLookupDebouncing(false);
        };
    }, [
        accountLookupCatalogsReady,
        formik.values.typeDocument,
        formik.values.document,
        readOnly,
    ]);

    return {
        formik,
        typesDocument,
        typesPerson,
        genders,
        isJuridical,
        loadingDocumentTypes,
        loadingPersonTypes,
        loadingGenders,
        documentTypesError,
        personTypesError,
        gendersError,
        isLookupDebouncing,
    };
};
